import { Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { environment } from '../../environments/environment';
import { Subject } from 'rxjs';
import { ILoggedInUserViewModel, ISignalREventResponse } from '../model/common.model';
import { EventMap, maxRetries, RoleEventMap, serverTimeoutInMilliseconds } from './event.constant';

@Injectable({
  providedIn: 'root',
})
export class SignalrService {
  public eventDataChange: Subject<any> = new Subject<any>();
  private _user: ILoggedInUserViewModel;
  private _hubConnection: signalR.HubConnection;
  private registrationRetryCount = 0;
  private retryTimeout: NodeJS.Timeout | null = null;
  private readonly emptyGuid = '00000000-0000-0000-0000-000000000000';

  constructor() {
    this._user = JSON.parse(localStorage.getItem('user'));
    this.startSignalRConnection();
  }

  public sseEventListener(eventName) {
    this._hubConnection.on(eventName, (data, extraParams) => {
      const signalRResponse: ISignalREventResponse = { data: data, eventName: eventName, extraParams: extraParams };
      console.log(signalRResponse);

      this.eventDataChange.next(signalRResponse);
    });
  }

  removeEventListener(eventName: string): void {
    this._hubConnection.off(eventName);
  }

  public stopConnection(): void {
    this.clearRetryTimeout();
    this._hubConnection?.stop().catch((err) => console.log('Error while stopping SignalR connection: ' + err));
  }

  private startSignalRConnection() {
    const id = this._user?.memberId ?? this.emptyGuid;
    this._hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${environment.sseEndpoint}` + 'sseHub?memberId=' + id)
      .withAutomaticReconnect()
      .build();

    this._hubConnection.serverTimeoutInMilliseconds = serverTimeoutInMilliseconds; //4 minutes

    this._hubConnection
      .start()
      .then(() => {
        this.registerEvents();
      })
      .catch((err) => {
        console.log('Error while starting connection: ' + err);
        console.log('Reconnecting signalR ....');
        setTimeout(() => {
          this.startSignalRConnection();
        }, 3000);
      });

    this._hubConnection.onreconnected((connectionId: string) => {
      console.log('SignalR is reconnected');
      this.registerEvents();
    });
  }

  private registerEvents() {
    const eventList: string[] = this._user?.role ? RoleEventMap[this._user.role] : EventMap;
    const id = this._user?.memberId ?? this.emptyGuid;
    this._hubConnection
      .invoke('RegisterSubscriber', id, eventList, [])
      .then(() => {
        console.log('SignalR events are registered successfully.');
        this.clearRetryTimeout();
      })
      .catch((reason) => {
        console.log('Event registration is failed with reason: ', reason);
        if (this.registrationRetryCount < maxRetries) {
          this.registrationRetryCount++;
          console.log(`Retrying event registration (attempt ${this.registrationRetryCount++})...`);
          if (!this.retryTimeout) {
            this.retryTimeout = setTimeout(() => {
              this.registerEvents();
            }, 30000);
          }
        } else {
          console.log(`Max retries (${maxRetries}) reached. Unable to register SignalR events.`);
          this.clearRetryTimeout();
        }
      });
  }

  private clearRetryTimeout() {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
      this.registrationRetryCount = 0;
    }
  }
}
